<template>
  <div class="miniapp-realtime-page" v-loading="isLoading">
    <header class="miniapp-header">
      <div class="miniapp-brand">
        <el-button type="text" icon="el-icon-arrow-left" @click="$router.push('/HelloWorld')">主页</el-button>
        <div class="miniapp-brand-title"><i class="el-icon-time"></i> 小程序实时收益</div>
      </div>
      <div class="miniapp-header-actions">
        <span><i class="el-icon-user"></i> {{ accountLabel }}</span>
        <el-button type="text" icon="el-icon-switch-button" @click="quit">退出</el-button>
      </div>
    </header>

    <main class="miniapp-content">
      <section class="miniapp-title-row">
        <div>
          <div class="miniapp-kicker">实时数据</div>
          <h1>小程序实时收益</h1>
          <p>直接查询抖音小程序最近 1 小时的聚合收益数据，通常延迟 10～15 分钟。</p>
        </div>
        <el-button type="primary" icon="el-icon-refresh" :loading="realtimeLoading" @click="queryRealtime">刷新数据</el-button>
      </section>

      <section class="miniapp-toolbar">
        <div class="miniapp-field">
          <label>选择小程序</label>
          <el-select v-model="selectedGame" filterable placeholder="请选择小程序" class="miniapp-select">
            <el-option v-for="game in miniApps" :key="game.gameName" :label="game.gameName" :value="game.gameName">
              <span>{{ game.gameName }}</span><small>{{ game.username || '当前账号' }}</small>
            </el-option>
          </el-select>
        </div>
        <div class="miniapp-toolbar-note" v-if="miniApps.length">共 {{ miniApps.length }} 个小程序</div>
        <el-button v-if="!miniApps.length" type="primary" plain icon="el-icon-setting" @click="$router.push('/account-manage')">去配置小程序</el-button>
      </section>

      <el-alert
        v-if="!miniApps.length && !isLoading"
        title="暂未配置小程序"
        description="请在伙伴管理中添加应用，平台类型选择“小程序”，填写抖音后台的 AppID 和 AppSecret。"
        type="info"
        show-icon
        :closable="false"
      ></el-alert>

      <template v-if="realtimeData">
        <section class="miniapp-cards">
          <div class="miniapp-card"><span>准实时广告消耗</span><strong>¥{{ formatCost(realtimeData.totalCostYuan) }}</strong></div>
          <div class="miniapp-card"><span>广告请求次数</span><strong>{{ formatInteger(realtimeData.totalIpu) }}</strong></div>
          <div class="miniapp-card"><span>ECPM</span><strong>接口未返回曝光 PV</strong></div>
          <div class="miniapp-card"><span>最近事件</span><strong>{{ realtimeData.latestEventTime || '--' }}</strong></div>
        </section>

        <section class="miniapp-panel">
          <div class="panel-heading"><strong>实时明细</strong><span>{{ realtimeData.recordCount || 0 }} 条</span></div>
          <el-table :data="realtimeData.records" stripe border class="miniapp-table" empty-text="暂无实时数据">
            <el-table-column prop="eventTime" label="事件时间" min-width="180"></el-table-column>
            <el-table-column prop="adType" label="广告类型" min-width="130"></el-table-column>
            <el-table-column prop="ipu" label="请求次数" width="120"></el-table-column>
            <el-table-column label="广告消耗（元）" width="160">
              <template slot-scope="scope">{{ formatCost(scope.row.costYuan) }}</template>
            </el-table-column>
          </el-table>
          <p class="miniapp-note">实时接口返回的是 5 分钟聚合数据；最终结算收入以抖音官方结算数据为准。当前接口未返回曝光 PV，因此不计算 ECPM。</p>
        </section>
      </template>
    </main>
  </div>
</template>

<script>
import http from "../api/http";

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default {
  name: "MiniAppRealtime",
  data() {
    return {
      role: localStorage.getItem("role"),
      games: [],
      selectedGame: "",
      realtimeData: null,
      isLoading: false,
      realtimeLoading: false,
      timer: null
    };
  },
  computed: {
    miniApps() {
      return this.games.filter(game => String(game.appType || "").toLowerCase() === "mini_app");
    },
    accountLabel() {
      if (this.role === "0") return "管理员";
      const game = this.games.find(item => item.gameName === this.selectedGame);
      return game && game.username ? game.username : "当前账号";
    }
  },
  watch: {
    selectedGame(value) {
      if (value) this.queryRealtime();
    }
  },
  methods: {
    authConfig() {
      return { headers: { token: `${localStorage.getItem("token")}` } };
    },
    fetchGames() {
      this.isLoading = true;
      http.get("/api/userGames/getUserGames", this.authConfig()).then(response => {
        if (!response.data || response.data.code !== 1) {
          this.$message.error(response.data && response.data.msg || "获取小程序列表失败");
          return;
        }
        this.games = Array.isArray(response.data.data) ? response.data.data : [];
        if (!this.miniApps.some(game => game.gameName === this.selectedGame)) {
          this.selectedGame = this.miniApps[0] ? this.miniApps[0].gameName : "";
        }
      }).catch(() => this.$message.error("获取小程序列表失败")).finally(() => { this.isLoading = false; });
    },
    realtimeRange() {
      const end = new Date();
      const start = new Date(end.getTime() - 60 * 60 * 1000);
      const format = value => `${formatLocalDate(value)} ${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
      return [format(start), format(end)];
    },
    async queryRealtime(showMessage = true) {
      if (!this.selectedGame) return;
      this.realtimeLoading = true;
      const [startDate, endDate] = this.realtimeRange();
      try {
        const response = await http.get("/api/ecpm/miniAppRealtime", {
          ...this.authConfig(), params: { gameName: this.selectedGame, startDate, endDate }
        });
        if (!response.data || response.data.code !== 1) {
          this.realtimeData = null;
          if (showMessage) this.$message.warning(response.data && response.data.msg || "小程序实时收益查询失败");
          return;
        }
        this.realtimeData = response.data.data;
      } catch (error) {
        this.realtimeData = null;
        if (showMessage) this.$message.error("小程序实时收益查询失败");
      } finally {
        this.realtimeLoading = false;
      }
    },
    formatInteger(value) {
      return value === null || value === undefined ? "--" : Number(value).toLocaleString("zh-CN");
    },
    formatCost(value) {
      if (value === null || value === undefined || value === "") return "0.00000";
      const number = Number(value);
      return Number.isFinite(number) ? number.toFixed(5) : "0.00000";
    },
    quit() {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      this.$router.push("/login");
    }
  },
  mounted() {
    this.fetchGames();
    this.timer = window.setInterval(() => { if (this.selectedGame && !this.realtimeLoading) this.queryRealtime(false); }, 60000);
  },
  beforeDestroy() {
    window.clearInterval(this.timer);
  }
};
</script>

<style scoped>
.miniapp-realtime-page { min-height: 100vh; color: #243b5a; background: #f4f7fb; }
.miniapp-header { height: 76px; padding: 0 42px; display: flex; align-items: center; justify-content: space-between; background: #fff; border-bottom: 1px solid #e7edf5; }
.miniapp-brand, .miniapp-header-actions { display: flex; align-items: center; gap: 22px; }
.miniapp-brand > .el-button { color: #5a7ca6; font-size: 16px; }
.miniapp-brand-title { color: #243b5a; font-size: 20px; font-weight: 700; }
.miniapp-brand-title i { color: #2f86e8; margin-right: 7px; }
.miniapp-header-actions { color: #6f87a7; font-size: 15px; }
.miniapp-header-actions .el-button { color: #6f87a7; }
.miniapp-content { max-width: 1440px; margin: 0 auto; padding: 42px 48px 80px; }
.miniapp-title-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
.miniapp-kicker { color: #2f86e8; font-size: 14px; letter-spacing: 2px; }
.miniapp-title-row h1 { margin: 8px 0 10px; color: #1d3557; font-size: 34px; }
.miniapp-title-row p { margin: 0; color: #8497b2; font-size: 15px; }
.miniapp-toolbar { display: flex; align-items: flex-end; gap: 16px; margin-bottom: 24px; padding: 22px 24px; background: #fff; border: 1px solid #e1e9f4; border-radius: 14px; box-shadow: 0 8px 22px rgba(49,83,126,.06); }
.miniapp-field { width: 420px; }
.miniapp-field label { display: block; margin-bottom: 8px; color: #7388a5; font-size: 13px; }
.miniapp-select { width: 100%; }
.miniapp-select :deep(.el-input__inner) { height: 42px; border-color: #c8d8ea; }
.miniapp-toolbar-note { margin-left: auto; padding-bottom: 11px; color: #8aa0ba; font-size: 13px; }
.miniapp-toolbar :deep(.el-select-dropdown__item small) { float: right; margin-left: 18px; color: #9aabc0; }
.miniapp-cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin-top: 24px; }
.miniapp-card { min-height: 112px; padding: 22px 24px; background: #fff; border: 1px solid #e0e9f4; border-radius: 14px; box-shadow: 0 8px 22px rgba(49,83,126,.06); }
.miniapp-card span { display: block; color: #8095b0; font-size: 14px; }
.miniapp-card strong { display: block; margin-top: 14px; color: #2574cf; font-size: 23px; }
.miniapp-panel { margin-top: 20px; padding: 22px 24px 18px; background: #fff; border: 1px solid #e0e9f4; border-radius: 14px; box-shadow: 0 8px 22px rgba(49,83,126,.06); }
.panel-heading { display: flex; justify-content: space-between; margin-bottom: 14px; color: #294566; font-size: 17px; }
.panel-heading span { color: #93a6bd; font-size: 13px; }
.miniapp-table :deep(th) { color: #6b82a0; background: #f7faff; }
.miniapp-note { margin: 14px 0 0; color: #8a9cb2; font-size: 13px; }
@media (max-width: 900px) { .miniapp-content { padding: 28px 20px 60px; } .miniapp-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); } .miniapp-field { width: 100%; } .miniapp-toolbar { align-items: stretch; flex-direction: column; } .miniapp-toolbar-note { margin-left: 0; padding-bottom: 0; } }
</style>
